import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Broom, Check, Clock, X, Camera, Link as LinkIcon, Clipboard, Eye } from '@phosphor-icons/react';

export default function CleaningPage() {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/cleaning').then(r => setTasks(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/cleaning/${id}/status`, { status });
    load();
  };

  const statusBadge = (s) => {
    if (s === 'completed') return { bg: 'bg-emerald-50 text-emerald-700', icon: Check };
    if (s === 'needs_verification') return { bg: 'bg-violet-50 text-violet-700', icon: Camera };
    if (s === 'in_progress') return { bg: 'bg-blue-50 text-blue-700', icon: Clock };
    if (s === 'cancelled') return { bg: 'bg-red-50 text-red-700', icon: X };
    return { bg: 'bg-amber-50 text-amber-700', icon: Clock };
  };

  const [copiedId, setCopiedId] = useState(null);
  const copyVerifyLink = (taskId) => {
    navigator.clipboard.writeText(`${window.location.origin}/verify-cleaning/${taskId}`);
    setCopiedId(taskId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div data-testid="cleaning-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Cleaning Schedule</h1>
        <p className="text-sm text-zinc-500 mt-1">Auto-created tasks from bookings. Manage turnover cleaning.</p>
      </div>

      {loading ? <p className="text-sm text-zinc-500">Loading...</p> : tasks.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center" data-testid="no-cleaning">
          <Broom size={40} weight="duotone" className="text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>No cleaning tasks</h3>
          <p className="text-sm text-zinc-500">Tasks are auto-created when bookings are added.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200" data-testid="cleaning-list">
          <div className="divide-y divide-zinc-100">
            {tasks.map((t, i) => {
              const badge = statusBadge(t.status);
              return (
                <div key={t.id} className="px-6 py-4 flex items-center justify-between hover:bg-zinc-50 transition-colors" data-testid={`cleaning-task-${i}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-zinc-900">{t.property_name}</p>
                      <span className={`text-xs px-2 py-0.5 font-bold ${badge.bg}`}>{t.status}</span>
                    </div>
                    <p className="text-xs text-zinc-500">{t.notes}</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Scheduled: {t.scheduled_date} {t.assignee && `| Assigned: ${t.assignee}`}
                      {t.photos_count > 0 && <span className="text-violet-600 font-bold"> | {t.photos_count} photo{t.photos_count > 1 ? 's' : ''} uploaded</span>}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4 items-center">
                    <button onClick={() => copyVerifyLink(t.id)} className="px-2 py-1.5 border border-zinc-200 text-zinc-500 hover:text-zinc-900 text-xs rounded-sm flex items-center gap-1" title="Copy photo upload link for cleaner" data-testid={`cleaning-verify-link-${i}`}>
                      {copiedId === t.id ? <><Check size={10} /> Copied</> : <><Camera size={12} /> Verify Link</>}
                    </button>
                    {t.status === 'pending' && (
                      <>
                        <button onClick={() => updateStatus(t.id, 'in_progress')} className="px-3 py-1.5 bg-[#0052FF] text-white text-xs font-bold rounded-sm hover:bg-[#003EDD]" data-testid={`cleaning-start-${i}`}>Start</button>
                        <button onClick={() => updateStatus(t.id, 'completed')} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-sm hover:bg-emerald-700" data-testid={`cleaning-complete-${i}`}>Complete</button>
                      </>
                    )}
                    {t.status === 'in_progress' && (
                      <button onClick={() => updateStatus(t.id, 'completed')} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-sm hover:bg-emerald-700" data-testid={`cleaning-complete-${i}`}>Mark Complete</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
